/**
 * Admin Subscriptions Screen.
 * Lists subscriptions with search and filter.
 */

import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { EmptyState } from '@/app/EmptyState';
import { Pagination } from '@/app/Pagination';
import { Modal } from '@/app/Modal';
import { classifyError } from '@/api/errors';
import * as subscriptionsApi from '@/api/subscriptionsApi';
import type { Subscription } from '@/api/types';

const STATUS_PILL_CLASS: Record<string, string> = {
  active: 'pill--active-sub',
  trial: 'pill--trial',
  grace_period: 'pill--warning',
  billing_issue: 'pill--warning',
  cancelled: 'pill--cancelled',
  expired: 'pill--expired',
};

export function SubscriptionsScreen() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);

  const limit = 20;

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionsApi.listSubscriptions({
        search: search || undefined,
        status: statusFilter || undefined,
        provider: providerFilter || undefined,
        limit,
        cursor: page > 1 ? String((page - 1) * limit) : undefined,
      });
      setSubscriptions(result.data);
      setTotal(result.pagination.total);
      setHasMore(result.pagination.has_more);
    } catch (err) {
      setError(classifyError(err).message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, providerFilter, page]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchSubscriptions();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Subscriptions"
        subtitle={`${total.toLocaleString()} total subscriptions`}
      />

      {/* Filter Bar */}
      <div className="filter-bar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
          <div className="search-input" style={{ position: 'relative', flex: 1 }}>
            <SearchIcon />
            <input
              type="text"
              className="input"
              placeholder="Search by user email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '36px', width: '100%' }}
            />
          </div>
          <button type="submit" className="btn btn--primary">
            Search
          </button>
        </form>

        <select
          className="select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="grace_period">Grace Period</option>
          <option value="billing_issue">Billing Issue</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>

        <select
          className="select"
          value={providerFilter}
          onChange={(e) => {
            setProviderFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Providers</option>
          <option value="app_store">App Store</option>
          <option value="google_play">Google Play</option>
          <option value="revenuecat">RevenueCat</option>
        </select>
      </div>

      {/* Error Banner */}
      {error && <div className="alert alert--error mb-16">{error}</div>}

      {/* Loading / Empty */}
      {loading ? (
        <LoadingOverlay />
      ) : subscriptions.length === 0 ? (
        <EmptyState message="No subscriptions found." />
      ) : (
        <>
          <div className="card">
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Provider</th>
                    <th>Status</th>
                    <th>Product</th>
                    <th>Expires</th>
                    <th>Auto Renew</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td>{sub.user?.username ?? sub.user_id.slice(0, 8) + '...'}</td>
                      <td>
                        <span className="pill">{sub.provider.replace('_', ' ')}</span>
                      </td>
                      <td>
                        <span className={`pill ${STATUS_PILL_CLASS[sub.status] || ''}`}>
                          {sub.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="muted">{sub.product_id ?? '—'}</td>
                      <td className="muted text-sm">{formatDate(sub.expires_at)}</td>
                      <td>
                        {sub.auto_renew !== null && (
                          <span
                            className={`pill ${sub.auto_renew ? 'pill--success' : 'pill--danger'}`}
                          >
                            {sub.auto_renew ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedSub(sub)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Subscription Detail Modal */}
      <Modal
        open={!!selectedSub}
        onClose={() => setSelectedSub(null)}
        title="Subscription Details"
        footer={
          <button className="btn btn--ghost" onClick={() => setSelectedSub(null)}>
            Close
          </button>
        }
      >
        {selectedSub && (
          <div className="stack">
            <div className="field">
              <label>Subscription ID</label>
              <input className="input" value={selectedSub.id} readOnly />
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>User</label>
                <input
                  className="input"
                  value={selectedSub.user?.username ?? selectedSub.user_id}
                  readOnly
                />
              </div>
              <div className="field flex-1">
                <label>Environment</label>
                <span className="pill">{selectedSub.environment}</span>
              </div>
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>Provider</label>
                <span className="pill">{selectedSub.provider.replace('_', ' ')}</span>
              </div>
              <div className="field flex-1">
                <label>Status</label>
                <span className={`pill ${STATUS_PILL_CLASS[selectedSub.status] || ''}`}>
                  {selectedSub.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="field">
              <label>Product ID</label>
              <input className="input" value={selectedSub.product_id ?? '—'} readOnly />
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Started</label>
                <input className="input" value={formatDate(selectedSub.started_at)} readOnly />
              </div>
              <div className="field">
                <label>Expires</label>
                <input className="input" value={formatDate(selectedSub.expires_at)} readOnly />
              </div>
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>Auto Renew</label>
                <input
                  className="input"
                  value={
                    selectedSub.auto_renew !== null ? (selectedSub.auto_renew ? 'Yes' : 'No') : '—'
                  }
                  readOnly
                />
              </div>
              <div className="field flex-1">
                <label>Entitlement ID</label>
                <input className="input" value={selectedSub.entitlement_id ?? '—'} readOnly />
              </div>
            </div>

            {selectedSub.original_transaction_id && (
              <div className="field">
                <label>Original Transaction ID</label>
                <input className="input" value={selectedSub.original_transaction_id} readOnly />
              </div>
            )}

            <div className="field">
              <label>Created</label>
              <input className="input" value={formatDate(selectedSub.created_at)} readOnly />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 16,
        height: 16,
        color: 'var(--color-text-muted)',
      }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
